import type {
  Announcement,
  AnnouncementAction,
  Theme,
  Traits,
} from './types';
import { targetingApplies, triggerFires } from './runtime';
import { renderInlineMarkdownInto } from './markdown';

// Announcement runtime - single-shot broadcast messages with two display
// variants (modal centred / banner top-of-page). Sits alongside the tour
// runtime + the checklist widget; owns its own shadow root so its CSS
// can't leak into / be polluted by either.
//
// Selection rule: at most ONE banner and ONE modal at a time. First
// match by server ordering (lastPublishedAt desc). Dismissed
// announcements have already been filtered out by the API; we only see
// candidates the end user hasn't dismissed.

type WidgetState = {
  host: HTMLElement | null;
  shadow: ShadowRoot | null;
  banner: Announcement | null;
  modal: Announcement | null;
  theme: Theme | null;
  available: Announcement[];
  traits: Traits;
  // First-render guards so we report a `view` exactly once per (end-user,
  // announcement) combo within this session - server-side analytics don't
  // dedupe per-load, so the SDK does.
  viewedIds: Set<string>;
  // When the active banner uses pushdown placement, we add a top
  // margin to <body> equal to the banner's height. This stash holds
  // the cleanup so we can restore the original margin on dismiss /
  // swap / teardown without losing track of any author-set value.
  bannerPushdownCleanup: (() => void) | null;
};

const widget: WidgetState = {
  host: null,
  shadow: null,
  banner: null,
  modal: null,
  theme: null,
  available: [],
  traits: {},
  viewedIds: new Set(),
  bannerPushdownCleanup: null,
};

export type AnnouncementCallbacks = {
  reportEvent: (id: string, action: AnnouncementAction) => void;
};

let callbacks: AnnouncementCallbacks | null = null;

const SHADOW_CSS = `
:host {
  --steppr-primary: #0f172a;
  font-size: 14px;
  line-height: 1.45;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
* {
  box-sizing: border-box;
  font-family: var(--steppr-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
}
button, input, textarea { font: inherit; cursor: pointer; }

/* ===== Banner ===== */
.banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2147483640;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--steppr-primary, #0f172a);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18);
  font-size: 13px;
  line-height: 1.45;
}
.banner.banner-inline { position: static; }
.banner .banner-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 12px;
  overflow: hidden;
}
.banner .banner-title {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.banner .banner-body {
  opacity: 0.9;
  overflow: hidden;
  text-overflow: ellipsis;
}
.banner .banner-cta {
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 120ms ease;
}
.banner .banner-cta:hover { background: rgba(255, 255, 255, 0.26); }
.banner .banner-cta-secondary {
  flex-shrink: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 120ms ease;
}
.banner .banner-cta-secondary:hover { background: rgba(255, 255, 255, 0.12); }
.banner .banner-close {
  flex-shrink: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border: none;
  font-size: 18px;
  line-height: 1;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.banner .banner-close:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
}

/* ===== Modal ===== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483641;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal {
  position: relative;
  width: min(100%, 440px);
  background: #ffffff;
  border-radius: var(--steppr-radius, 12px);
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.32);
  padding: 28px 28px 24px;
  color: #0f172a;
}
.modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: #64748b;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 20px;
  line-height: 1;
}
.modal-close:hover { background: #f1f5f9; color: #0f172a; }
.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
}
.modal-body {
  font-size: 14px;
  line-height: 1.55;
  color: #475569;
  white-space: pre-wrap;
  margin: 0 0 20px;
}
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.modal-cta {
  background: var(--steppr-primary, #0f172a);
  color: #ffffff;
  border: 1px solid var(--steppr-primary, #0f172a);
  border-radius: var(--steppr-radius, 8px);
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
}
.modal-cta:hover { filter: brightness(1.1); }
.modal-cta-secondary {
  background: transparent;
  color: var(--steppr-primary, #0f172a);
  border: 1px solid #cbd5e1;
  border-radius: var(--steppr-radius, 8px);
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
}
.modal-cta-secondary:hover { background: #f1f5f9; }

/* ===== Powered by ===== */
.powered {
  margin-top: 14px;
  font-size: 11px;
  color: #94a3b8;
  text-align: right;
}
.powered a { color: inherit; text-decoration: none; }
.powered a:hover { text-decoration: underline; }
.banner .powered {
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
}
`;

function ensureShadow(): ShadowRoot {
  if (widget.shadow) return widget.shadow;
  const host = document.createElement('div');
  host.id = '__steppr_announcements__';
  host.style.all = 'initial';
  // Override display so an inline banner inside the shadow can occupy
  // real block-level space. all: initial resets <div> back to inline.
  host.style.display = 'block';
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = SHADOW_CSS;
  shadow.appendChild(style);
  document.body.appendChild(host);
  widget.host = host;
  widget.shadow = shadow;
  // Match the runtime / checklist pattern: stop pointer + focus events
  // from leaking out so click-outside / focus-trap libraries on the
  // customer's modals don't react to interactions inside ours.
  for (const evt of [
    'click',
    'mousedown',
    'mouseup',
    'pointerdown',
    'pointerup',
    'touchstart',
  ]) {
    host.addEventListener(evt, (e) => e.stopPropagation());
  }
  if (widget.theme) applyAnnouncementTheme(widget.theme);
  return shadow;
}

export function applyAnnouncementTheme(theme: Theme) {
  widget.theme = theme;
  if (!widget.shadow) return;
  let style = widget.shadow.getElementById(
    'steppr-announcement-theme',
  ) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'steppr-announcement-theme';
    widget.shadow.appendChild(style);
  }
  // Mirror the runtime / checklist pattern: pin the full theme via CSS
  // vars on :host so the existing static rules resolve correctly.
  // Without --steppr-font-family the universal selector falls back to
  // the system stack - fine, but the customer's pick wins when set.
  const radius =
    theme.radius === 'sm' ? '6px' : theme.radius === 'md' ? '10px' : '14px';
  const fontFamily =
    theme.fontFamily === 'inherit'
      ? 'inherit'
      : "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
  style.textContent = `:host { --steppr-primary: ${theme.primaryColor}; --steppr-radius: ${radius}; --steppr-font-family: ${fontFamily}; }`;
}

// Pick at most one banner + one modal that match targeting + triggers for
// the current URL. The server now includes already-dismissed announcements
// (so the Resource Center can list history) - we filter them here at the
// rendering layer instead. First match wins per display variant.
function pickEligible(
  list: Announcement[],
  url: string,
  traits: Traits,
): { banner: Announcement | null; modal: Announcement | null } {
  let banner: Announcement | null = null;
  let modal: Announcement | null = null;
  for (const a of list) {
    if (banner && modal) break;
    if (a.state?.dismissed) continue;
    if (!targetingApplies(a.snapshot.targeting, url, traits)) continue;
    if (
      !triggerFires(a.snapshot.triggers, {
        url,
        kind: 'page_load',
      })
    ) {
      continue;
    }
    if (a.snapshot.display === 'banner' && !banner) banner = a;
    else if (a.snapshot.display === 'modal' && !modal) modal = a;
  }
  return { banner, modal };
}

// Reserve vertical space for the banner by pushing the customer's body
// down. We track the prior inline marginTop so dismissal restores
// whatever the author had set (likely empty, but never assume).
// ResizeObserver keeps the margin in sync as the banner wraps / the
// viewport changes - single-line vs two-line banners differ by ~20px.
function applyBannerPushdown(banner: HTMLElement) {
  clearBannerPushdown();
  const body = document.body;
  if (!body) return;
  const priorMarginTop = body.style.marginTop;
  const priorTransition = body.style.transition;
  // Subtle ease so the page doesn't visibly snap on first paint.
  body.style.transition = `${priorTransition ? priorTransition + ', ' : ''}margin-top 160ms ease`;
  const update = () => {
    body.style.marginTop = `${banner.offsetHeight}px`;
  };
  update();
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(update);
    ro.observe(banner);
  }
  const onResize = () => update();
  window.addEventListener('resize', onResize);
  widget.bannerPushdownCleanup = () => {
    ro?.disconnect();
    window.removeEventListener('resize', onResize);
    body.style.marginTop = priorMarginTop;
    body.style.transition = priorTransition;
  };
}

function clearBannerPushdown() {
  widget.bannerPushdownCleanup?.();
  widget.bannerPushdownCleanup = null;
}

// Place the shadow host where a given placement needs it. For inline
// banners we want it at the top of <body> so the banner sits above
// everything and pushes content down naturally; for fixed placements
// (overlay / pushdown / modal-only) the host can sit at the end where
// it doesn't disturb document order.
function placeHost(placement: 'overlay' | 'pushdown' | 'inline') {
  const host = widget.host;
  const body = document.body;
  if (!host || !body) return;
  if (placement === 'inline') {
    if (body.firstChild !== host) body.insertBefore(host, body.firstChild);
  } else {
    if (body.lastChild !== host) body.appendChild(host);
  }
}

function renderBanner(a: Announcement) {
  if (!widget.shadow) return;
  // Per-flow theme overrides the session theme, same pattern as the
  // tour runtime + checklist widget.
  if (a.snapshot.theme) applyAnnouncementTheme(a.snapshot.theme);
  // Remove any prior banner first - clearing pushdown too so the new
  // banner doesn't double-up on body margin.
  clearBannerPushdown();
  widget.shadow.querySelector('.banner')?.remove();

  const placement = a.snapshot.config?.bannerPlacement ?? 'overlay';
  placeHost(placement);

  const root = document.createElement('div');
  root.className = placement === 'inline' ? 'banner banner-inline' : 'banner';

  const content = document.createElement('div');
  content.className = 'banner-content';

  if (a.snapshot.title) {
    const title = document.createElement('span');
    title.className = 'banner-title';
    title.textContent = a.snapshot.title;
    content.appendChild(title);
  }
  if (a.snapshot.body) {
    const body = document.createElement('span');
    body.className = 'banner-body';
    renderInlineMarkdownInto(body, a.snapshot.body);
    content.appendChild(body);
  }
  root.appendChild(content);

  // Secondary CTA renders to the LEFT of the primary so the eye lands on the
  // higher-stakes button last. Both buttons share `ctaTarget` semantics from
  // config (same-tab vs new-tab).
  if (a.snapshot.secondaryCtaLabel && a.snapshot.secondaryCtaUrl) {
    const sec = document.createElement('button');
    sec.type = 'button';
    sec.className = 'banner-cta-secondary';
    sec.textContent = a.snapshot.secondaryCtaLabel;
    sec.addEventListener('click', () => handleCtaClick(a, a.snapshot.secondaryCtaUrl!));
    root.appendChild(sec);
  }

  if (a.snapshot.ctaLabel && a.snapshot.ctaUrl) {
    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'banner-cta';
    cta.textContent = a.snapshot.ctaLabel;
    cta.addEventListener('click', () => handleCtaClick(a, a.snapshot.ctaUrl!));
    root.appendChild(cta);
  }

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'banner-close';
  close.setAttribute('aria-label', 'Dismiss');
  close.textContent = '×';
  close.addEventListener('click', () => dismiss(a, 'banner'));
  root.appendChild(close);

  widget.shadow.appendChild(root);

  if (placement === 'pushdown') {
    applyBannerPushdown(root);
  }
}

function renderModal(a: Announcement) {
  if (!widget.shadow) return;
  if (a.snapshot.theme) applyAnnouncementTheme(a.snapshot.theme);
  widget.shadow.querySelector('.modal-backdrop')?.remove();

  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  // Backdrop click opt-in: same flag as tours. Default is "X / CTA only"
  // so misclicks don't dismiss an announcement the author cared about.
  if (a.snapshot.config?.dismissOnBackdropClick === true) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) dismiss(a, 'modal');
    });
  }

  const card = document.createElement('div');
  card.className = 'modal';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'modal-close';
  close.setAttribute('aria-label', 'Dismiss');
  close.textContent = '×';
  close.addEventListener('click', () => dismiss(a, 'modal'));
  card.appendChild(close);

  if (a.snapshot.title) {
    const title = document.createElement('h2');
    title.className = 'modal-title';
    title.textContent = a.snapshot.title;
    card.appendChild(title);
  }
  if (a.snapshot.body) {
    const body = document.createElement('p');
    body.className = 'modal-body';
    renderInlineMarkdownInto(body, a.snapshot.body);
    card.appendChild(body);
  }

  const hasPrimaryCta = a.snapshot.ctaLabel && a.snapshot.ctaUrl;
  const hasSecondaryCta = a.snapshot.secondaryCtaLabel && a.snapshot.secondaryCtaUrl;
  if (hasPrimaryCta || hasSecondaryCta) {
    const actions = document.createElement('div');
    actions.className = 'modal-actions';
    if (hasSecondaryCta) {
      const sec = document.createElement('button');
      sec.type = 'button';
      sec.className = 'modal-cta-secondary';
      sec.textContent = a.snapshot.secondaryCtaLabel!;
      sec.addEventListener('click', () => handleCtaClick(a, a.snapshot.secondaryCtaUrl!));
      actions.appendChild(sec);
    }
    if (hasPrimaryCta) {
      const cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'modal-cta';
      cta.textContent = a.snapshot.ctaLabel!;
      cta.addEventListener('click', () => handleCtaClick(a, a.snapshot.ctaUrl!));
      actions.appendChild(cta);
    }
    card.appendChild(actions);
  }

  if (!widget.theme?.hideBranding) {
    const powered = document.createElement('div');
    powered.className = 'powered';
    powered.innerHTML =
      'Powered by <a href="https://steppr.io" target="_blank" rel="noopener noreferrer">Steppr</a>';
    card.appendChild(powered);
  }

  backdrop.appendChild(card);
  widget.shadow.appendChild(backdrop);
}

function reportViewIfNew(a: Announcement) {
  if (widget.viewedIds.has(a.id)) return;
  widget.viewedIds.add(a.id);
  callbacks?.reportEvent(a.id, 'view');
}

function dismiss(a: Announcement, kind: 'banner' | 'modal') {
  callbacks?.reportEvent(a.id, 'dismiss');
  if (kind === 'banner') {
    widget.banner = null;
    widget.shadow?.querySelector('.banner')?.remove();
    clearBannerPushdown();
  } else {
    widget.modal = null;
    widget.shadow?.querySelector('.modal-backdrop')?.remove();
  }
}

function handleCtaClick(a: Announcement, href: string) {
  // Both primary and secondary CTAs fire the same `cta_click` action for
  // analytics in v1. We can split this later (cta_secondary_click action +
  // a separate webhook event type) once we have customer demand for the
  // attribution. The button's text is recoverable from the snapshot anyway.
  callbacks?.reportEvent(a.id, 'cta_click');
  if (!href) return;
  // window.open must run synchronously inside the click handler or
  // popup blockers reject it; setTimeout would be enough to fail.
  // Same-tab nav stays on the original microtask trick so the
  // analytics fetch has a tick to flush before the page unloads.
  if (a.snapshot.config?.ctaTarget === 'blank') {
    window.open(href, '_blank', 'noopener,noreferrer');
  } else {
    setTimeout(() => {
      window.location.href = href;
    }, 0);
  }
}

export function bootAnnouncements(
  list: Announcement[],
  cbs: AnnouncementCallbacks,
  theme: Theme | null,
  url: string,
  traits: Traits,
) {
  callbacks = cbs;
  widget.available = list;
  widget.traits = traits;
  if (theme) widget.theme = theme;
  showEligible(url);
}

function showEligible(url: string) {
  const eligible = pickEligible(widget.available, url, widget.traits);

  // If the eligible result for either slot has changed, swap or remove.
  if (eligible.banner?.id !== widget.banner?.id) {
    if (eligible.banner) {
      ensureShadow();
      widget.banner = eligible.banner;
      renderBanner(eligible.banner);
      reportViewIfNew(eligible.banner);
    } else if (widget.banner) {
      widget.banner = null;
      widget.shadow?.querySelector('.banner')?.remove();
      clearBannerPushdown();
    }
  }
  if (eligible.modal?.id !== widget.modal?.id) {
    if (eligible.modal) {
      ensureShadow();
      widget.modal = eligible.modal;
      renderModal(eligible.modal);
      reportViewIfNew(eligible.modal);
    } else if (widget.modal) {
      widget.modal = null;
      widget.shadow?.querySelector('.modal-backdrop')?.remove();
    }
  }
  // Tear the shadow host down entirely if neither slot is rendering - keeps
  // the customer's DOM clean when no announcement matches.
  if (!widget.banner && !widget.modal && widget.host) {
    clearBannerPushdown();
    widget.host.remove();
    widget.host = null;
    widget.shadow = null;
  }
}

// Re-evaluate when the customer's app navigates client-side. Mirrors
// `onRouteChange` in checklist.ts.
export function onAnnouncementRouteChange(url: string) {
  showEligible(url);
}

// Re-evaluate when traits change (e.g. after Steppr.identify).
export function onAnnouncementTraitsChange(url: string, traits: Traits) {
  widget.traits = traits;
  showEligible(url);
}

export function teardownAnnouncements() {
  clearBannerPushdown();
  if (widget.host) widget.host.remove();
  widget.host = null;
  widget.shadow = null;
  widget.banner = null;
  widget.modal = null;
  widget.theme = null;
  widget.available = [];
  widget.traits = {};
  widget.viewedIds.clear();
}

/**
 * Force-render an announcement by id, bypassing dismiss state + targeting.
 * Used by the Resource Center "click to open" flow when a visitor wants to
 * re-read a previously-dismissed announcement. Re-uses the same rendering
 * paths as the auto-eligible flow so styling stays identical.
 */
export function openAnnouncementById(id: string): void {
  const target = widget.available.find((a) => a.id === id);
  if (!target) return;
  ensureShadow();
  if (target.snapshot.display === 'modal') {
    if (widget.modal?.id !== target.id) {
      widget.shadow?.querySelector('.modal-backdrop')?.remove();
    }
    widget.modal = target;
    renderModal(target);
  } else {
    if (widget.banner?.id !== target.id) {
      widget.shadow?.querySelector('.banner')?.remove();
      clearBannerPushdown();
    }
    widget.banner = target;
    renderBanner(target);
  }
}
